/**
 * 收银台文案配置。
 *
 * 放在 shared 里让前后端共用同一份默认值：
 * 后端用它做兼容回退，后台页面用它展示“默认文案”提示。
 *
 * 占位符：
 *   {out_trade_no} 会被替换为商户订单号
 *   {seconds}      只用于 paid_redirect，会被替换为自动返回的剩余秒数
 */
export interface CheckoutCopy {
  /** 品牌区 */
  brand_name: string;
  brand_tagline: string;
  /** 金额分位提示 */
  surcharge_lead: string;
  surcharge_note: string;
  /** 扫码指引 */
  guide_wxpay: string;
  guide_alipay_deeplink: string;
  guide_alipay_qr: string;
  guide_transfer: string;
  /** 微信支付操作步骤（每行一步）。 */
  wxpay_steps: string;
  /** 微信支付注意事项（每行一条）。 */
  wxpay_notice: string;
  /** 移动端长按识别提示。 */
  wxpay_mobile_tip: string;
  /** 状态提示 */
  expired_note: string;
  aside_note: string;
  /** 支付成功页 */
  paid_title: string;
  paid_note: string;
  paid_return: string;
  /** 支付成功后自动返回的倒计时提示，可用 {seconds} 占位。 */
  paid_redirect: string;
  /** 订单已结束页 */
  ended_title: string;
  ended_note: string;
  /** 订单信息区 */
  aside_title: string;
  label_trade_no: string;
  label_created_at: string;
  label_remaining: string;
  /** 移动端按钮 */
  button_open_wxpay: string;
  button_open_alipay: string;
}

export const DEFAULT_CHECKOUT_COPY: CheckoutCopy = {
  brand_name: "Dubhe",
  brand_tagline: "安全收银台",
  surcharge_lead: "请按上方金额准确支付。",
  surcharge_note: " 金额精确到分，多付或少付都无法自动确认。",
  guide_wxpay: "请用微信扫描上方二维码完成支付，支付结果会自动同步。",
  guide_alipay_deeplink: "点击上方按钮直达支付宝付款，也可长按二维码识别。金额请按上方显示输入。",
  guide_alipay_qr: "打开支付宝扫描上方二维码，并输入显示的金额。",
  guide_transfer: "打开支付宝扫码转账，备注请保持为 {out_trade_no}。",
  wxpay_steps: "打开微信，点右上角「+」→ 扫一扫\n对准上方二维码扫描\n确认金额后完成支付",
  wxpay_notice: "请务必按上方金额付款，金额不一致将无法自动到账\n支付完成后请勿关闭本页，系统会自动确认\n已支付请耐心等待，切勿重复付款",
  wxpay_mobile_tip: "在微信内可长按二维码识别；也可先保存图片，再到微信「扫一扫 → 相册」中选择",
  expired_note: "如果你已经付款，请稍候，系统仍在确认中。如尚未付款，请返回商户重新下单。",
  aside_note: "支付完成后本页面会自动确认，请不要重复支付。",
  paid_title: "支付成功",
  paid_note: "订单 {out_trade_no} 已完成。",
  paid_return: "返回商户页面",
  paid_redirect: "{seconds} 秒后自动返回",
  ended_title: "订单已结束",
  ended_note: "未收到这笔款项。请不要继续付款，返回商户重新下单。",
  aside_title: "订单信息",
  label_trade_no: "订单号",
  label_created_at: "创建时间",
  label_remaining: "自动确认剩余",
  button_open_wxpay: "打开微信付款",
  button_open_alipay: "打开支付宝付款",
};

export const CHECKOUT_COPY_KEYS = Object.keys(DEFAULT_CHECKOUT_COPY) as Array<keyof CheckoutCopy>;

/** 合并存储值，缺失字段回落到默认值，保证旧数据不会让页面出现空白。 */
export function mergeCheckoutCopy(raw: unknown): CheckoutCopy {
  const source = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const result = { ...DEFAULT_CHECKOUT_COPY };
  for (const key of CHECKOUT_COPY_KEYS) {
    const value = source[key];
    if (typeof value === "string" && value.trim() !== "") result[key] = value;
  }
  return result;
}

/** 替换文案中的占位符。 */
export function fillCopy(text: string, values: Record<string, string>) {
  return text.replace(/\{(\w+)\}/g, (match, key: string) => values[key] ?? match);
}
